import React, {useEffect, useState} from "react";
import {ConfigProvider, DatePicker} from "antd";
import {default as NL_locale} from "antd/lib/locale/nl_NL";
import {default as SK_locale} from "antd/lib/locale/sk_SK";
import {default as HU_locale} from "antd/lib/locale/hu_HU";
import {default as EN_locale} from "antd/lib/locale/en_GB";
import {default as DE_locale} from "antd/lib/locale/de_DE";
import dayjs, {Dayjs} from "dayjs";
import {LOCALE} from "helpers/config";
import clsx from "clsx";
import {useDebouncedCallback} from "use-debounce";
import Dates from "utils/Dates";
import {DateRange} from "types/entities/DateRange";
import UiFilterLabel from "pages/Front/components/Ui/components/Filter/UiFilterLabel";
import TableFilterRemoveButton
    from "pages/Front/components/Tanstack/components/Filters/components/TableFilterRemoveButton";

const localeObject = locale => {
    switch (locale) {
        case "nl-NL":
            dayjs.locale("nl");
            return NL_locale;
        case "sk-SK":
            dayjs.locale("sk");
            return SK_locale;
        case "hu-HU":
            dayjs.locale("hu");
            return HU_locale;
        case "en-GB":
            dayjs.locale("en");
            return EN_locale;
        case "de":
        case "de-CH":
            dayjs.locale("de-ch");
            return DE_locale;
        default:
            return EN_locale;
    }
};

interface UiDateRangePickerProps {
    label?: string;
    className?: string;
    selectedDay: DateRange | null;
    onChange: (date: DateRange | null) => void;
    format?: string;
    disabled?: boolean;
}

const UiDateRangePicker = (props: UiDateRangePickerProps) => {
    const {RangePicker} = DatePicker;
    const getFormattedDate = (newDate: DateRange): [Dayjs, Dayjs] => {
        return [
            dayjs(Dates.date(newDate.from , "", "YYYY-MM-DD"), "YYYY-MM-DD"),
            dayjs(Dates.date(newDate.to , "", "YYYY-MM-DD"), "YYYY-MM-DD")
        ];
    }
    const [selectedDate, setSelectedDate] = useState(props.selectedDay !== null
        ? getFormattedDate(props.selectedDay)
        : null
    );
    const [isFocused, setIsFocused] = useState(false);
    const debouncedChangeHandler = useDebouncedCallback((data) => props.onChange(data), 300);

    const datePickerHandler = (date: any[]): void => {
        const dateSelected = {
            from: new Date(date[0].toString()),
            to: new Date(date[1].toString()),
        };
        debouncedChangeHandler(dateSelected)
    };

    const removeFiltersHandler = () => {
        debouncedChangeHandler(null);
    }

    useEffect(() => {
        const newDate = props.selectedDay !== null ? getFormattedDate(props.selectedDay) : null;
        if (newDate !== selectedDate) {
            setSelectedDate(newDate);
        }
    }, [props.selectedDay]);

    return (
        <div className={clsx(
            "UiDatePicker flex flex-col w-auto",
            props.className
        )}>
            {props.label && (
                <div className={"relative flex flex-row items-center"}>
                    <UiFilterLabel label={props.label} />
                    <TableFilterRemoveButton className={"!relative !right-auto mb-2"} inputValue={selectedDate} onClick={removeFiltersHandler} />
                </div>
            )}
            <div className={"flex flex-row items-center shadow-sm shadow-black/10 rounded relative"}>
                <ConfigProvider locale={localeObject(LOCALE)}>
                    <RangePicker
                        allowClear={false}
                        className={clsx(
                            "[&>div>span>span>svg]:!w-5 [&>div>span>span>svg]:!h-5 [&>div>span>span>svg]:text-gray-800 [&>div>span>span>svg]:hover:text-blue-400 [&>div>input]:hover:text-blue-400 [&>div>input]:!font-medium [&>div>input]:!border-0 [&>div>input]:!px-0 [&>div>input]:!font-circular [&>div>input]:!py-0 [&>div>input]:!text-sm !px-5 !pt-[0.5rem] !pb-[0.5rem] inline-flex items-center !shadow-sm !shadow-black/10 !rounded !ring-1 !transition !duration-150 hover:!bg-white [&>div>input]:placeholder:!text-gray-800",
                            selectedDate ? "[&>div>input]:!text-blue-400 !bg-white" : " [&>div>input]:!text-gray-800 !bg-white",
                            isFocused ? "!ring-blue-400 !bg-white [&>div>span>span>svg]:!text-blue-400" : "!ring-gray-500"
                        )}
                        /*@ts-ignore*/
                        defaultValue={selectedDate}
                        disabled={props.disabled}
                        dropdownClassName={""}
                        format={props.format ? props.format : "D.M.YYYY"}
                        style={{
                            borderWidth: 0,

                        }}
                        value={selectedDate}
                        /*@ts-ignore*/
                        onChange={datePickerHandler}
                        onOpenChange={isOpen => {
                            setIsFocused(isOpen)
                        }}
                        dropdownAlign={{
                            points: ['tl', 'bl'],
                        }}
                        popupClassName={"!absolute !top-[34px] !left-auto"}
                        getPopupContainer={(triggerNode) => {
                            return triggerNode.parentNode as HTMLElement;
                        }}
                    />
                </ConfigProvider>
                {selectedDate && (
                    <TableFilterRemoveButton className={"!right-11 border-r -mr-1 pr-0.5 z-10"} inputValue={selectedDate} onClick={removeFiltersHandler} />
                )}
            </div>
        </div>
    );
};

export default UiDateRangePicker;